import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FiPlay,
  FiPause,
  FiClock,
  FiUsers,
  FiStar,
  FiShoppingCart,
  FiVideo,
  FiBookOpen,
  FiInfo,
  FiTarget,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiSearch
} from 'react-icons/fi';
import { cartService } from '../services/cartService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnrollmentButton from '../components/EnrollmentButton';
import SearchField from '../components/SearchField';
import { apiUrls, getHttpHeaders } from '../config/api';

// CSS Variables
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#FFA500',
  accentColor: '#28a745',
  textPrimary: '#2d3748',
  textLight: '#718096',
  textDark: '#1a202c',
  background: '#f7fafc',
  white: '#ffffff',
  borderColor: '#e2e8f0',
  shadowLight: '0 2px 4px rgba(0, 0, 0, 0.1)',
  shadowMedium: '0 4px 6px rgba(0, 0, 0, 0.1)',
  shadowHeavy: '0 8px 16px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px'
  }
};

// Styled Components for Enhanced UX
const CarouselCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(37, 60, 140, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  max-width: 1200px;
  width: 100%;
  display: grid;
  grid-template-columns: 240px 1fr 320px;
  gap: 24px;
  align-items: start;
  min-height: 360px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${CSS_VARS.primaryColor} 0%, ${CSS_VARS.secondaryColor} 100%);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 48px rgba(37, 60, 140, 0.15);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: center;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const BookImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const PriceTag = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${CSS_VARS.primaryColor};
  text-align: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%);
  padding: 12px 20px;
  border-radius: 24px;
  border: 3px solid ${CSS_VARS.primaryColor};
  box-shadow: 0 4px 16px rgba(37, 60, 140, 0.1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 24px;
    background: linear-gradient(135deg, ${CSS_VARS.primaryColor} 0%, ${CSS_VARS.secondaryColor} 100%);
    z-index: -1;
  }
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  border-radius: 16px;
  padding: 16px;
  border: 2px solid rgba(37, 60, 140, 0.1);
  transition: all 0.3s ease;
  position: relative;
  min-height: 320px;

  &.video-active {
    background: linear-gradient(135deg, #e8f0fe 0%, #dbeafe 100%);
    border-color: rgba(37, 60, 140, 0.3);
    transform: scale(1.02);
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
`;

const ContentHeader = styled.div`
  margin-bottom: 24px;
`;

const ContentTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 12px;
  line-height: 1.3;
`;

const ContentSubtitle = styled.div`
  font-size: 1.1rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 16px;
  font-weight: 500;
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const ContentDescription = styled.p`
  color: ${CSS_VARS.textPrimary};
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 24px;
  flex-grow: 1;
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(37, 60, 140, 0.04);
  border-radius: 12px;
  border-left: 4px solid ${CSS_VARS.primaryColor};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: ${CSS_VARS.textLight};
  font-weight: 500;

  svg {
    color: ${CSS_VARS.primaryColor};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
`;

const SecondaryButton = styled.button`
  padding: 12px 20px;
  background: transparent;
  color: ${CSS_VARS.primaryColor};
  border: 2px solid ${CSS_VARS.primaryColor};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${CSS_VARS.primaryColor};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 60, 140, 0.2);
  }
`;

// Real location service implementation 
const locationService = {
  getCountries: async () => {
    try {
      const response = await fetch(`${apiUrls.countries.list}?active=true`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('🌍 Countries API response:', data);
      // Backend returns array directly, not wrapped in object
      return { success: true, data: Array.isArray(data) ? data : data.countries || [] };
    } catch (error) {
      console.error('Error fetching countries:', error);
      return { success: false, data: [] };
    }
  },
  getStates: async () => {
    try {
      // Note: This method is deprecated - use getStatesByCountry instead
      console.log('⚠️ getStates() called - should use getStatesByCountry()');
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error fetching states:', error);
      return { success: false, data: [] };
    }
  },
  getStatesByCountry: async (countryId: string) => {
    try {
      const response = await fetch(apiUrls.countries.states(countryId));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log(`🏛️ States for country ${countryId}:`, data);
      // Backend returns array directly, not wrapped in object
      return { success: true, data: Array.isArray(data) ? data : data.states || [] };
    } catch (error) {
      console.error('Error fetching states by country:', error);
      return { success: false, data: [] };
    }
  }
};

// Real data service implementation
const dataService = {
  getGrades: async () => {
    try {
      const response = await fetch(apiUrls.books.grades);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('📊 Grades API response:', data);
      // Backend returns array directly, not wrapped in object
      return { success: true, data: Array.isArray(data) ? data : data.grades || [] };
    } catch (error) {
      console.error('Error fetching grades:', error);
      return { success: false, data: [] };
    }
  }
};

// TypeScript Interfaces
interface ContentPackagePageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  onPackageDetailClick?: (packageId: number) => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface ContentPackage {
  id: number;
  name: string;
  description: string;
  price: number;
  discount_price?: number;
  duration_months: number;
  max_users: number;
  image?: string;
  grade_id: number;
  grade_name?: string;
  features?: string;
  approval_status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  packageBooks?: Array<{
    book: {
      id: number;
      title: string;
      image?: string;
      description?: string;
      price?: number;
      discount_price?: number;
      video_link?: string;
    };
  }>;
  isSubscribed?: boolean; // Will be set based on user subscription status
  type: 'package'; // To distinguish from books
}

interface StandaloneBook {
  id: number;
  title: string;
  description: string;
  price: number;
  discount_price?: number;
  image?: string;
  video_link?: string;
  grade_id: number;
  grade_name?: string;
  subject_id?: number;
  subject_name?: string;
  created_at: string;
  updated_at: string;
  isPurchased?: boolean; // Will be set based on user purchase status
  type: 'book'; // To distinguish from packages
}

// Union type for displaying both packages and books
type ContentItem = ContentPackage | StandaloneBook;

interface Country {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
  country_id: number;
}

interface Grade {
  id: number;
  name: string;
}

interface FilterState {
  search: string;
  grade: string;
  country: string;
  state: string;
}

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 140px 20px 40px 20px;
`;

const FilterMain = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
`;

const FilterTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${CSS_VARS.textPrimary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FormSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: ${CSS_VARS.textPrimary};
  background: white;
  transition: all 0.3s ease;
  height: 44px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${CSS_VARS.primaryColor};
    box-shadow: 0 0 0 3px rgba(37, 60, 140, 0.1);
  }

  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    border-color: ${CSS_VARS.primaryColor};
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  font-size: 1.1rem;
  color: ${CSS_VARS.textLight};
`;

const ErrorState = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${CSS_VARS.textLight};
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: ${CSS_VARS.textPrimary};
  }
  
  p {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const ContentPackagePage: React.FC<ContentPackagePageProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick,
  onFaqClick,
  onFeedbackClick,
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  onPackageDetailClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  // Helper function to strip HTML tags for preview
  const stripHtmlTags = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };
  // Filter states
  const DEFAULT_COUNTRY_ID = '230'; // United States (used for dropdown default only)

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    grade: '',
    country: DEFAULT_COUNTRY_ID,
    state: ''
  });

  // Cart feedback state
  const [addedToCart, setAddedToCart] = useState<Set<number>>(new Set());

  // Data states
  const [packages, setPackages] = useState<ContentPackage[]>([]);
  const [books, setBooks] = useState<StandaloneBook[]>([]);
  const [allContent, setAllContent] = useState<ContentItem[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);

  // UI states
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Carousel state and controls  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [isVideoInteracting, setIsVideoInteracting] = useState(false);
  const [videoInteractionTimer, setVideoInteractionTimer] = useState<NodeJS.Timeout | null>(null);
  const packagesPerPage = 1; // Show only one package at a time
  
  // Get content items for current page (one item per slide)
  const startIndex = currentSlide;
  const visibleContent = allContent.slice(startIndex, startIndex + 1);

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, allContent.length - 1));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Video interaction handlers
  const handleVideoInteraction = () => {
    console.log('🎬 Video interaction detected - pausing carousel');
    setIsVideoInteracting(true);
    setIsCarouselPaused(true);
    
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
    }
  };

  const handleVideoInteractionEnd = () => {
    console.log('🎬 Video interaction ended - resuming carousel in 3 minutes');
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
    }
    
    // Resume carousel after 3 minutes of no interaction
    const timer = setTimeout(() => {
      console.log('🎬 Resuming carousel after video interaction timeout');
      setIsVideoInteracting(false);
      setIsCarouselPaused(false);
    }, 180000); // 3 minutes = 180,000 milliseconds
    
    setVideoInteractionTimer(timer);
  };

  // Reset video interaction state when slide changes
  useEffect(() => {
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
      setVideoInteractionTimer(null);
    }
    setIsVideoInteracting(false);
    setIsCarouselPaused(false);
  }, [currentSlide, videoInteractionTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (videoInteractionTimer) {
        clearTimeout(videoInteractionTimer);
      }
    };
  }, [videoInteractionTimer]);

  // Reset carousel when content changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [allContent]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if the user is interacting with video (common video control keys)
      if (event.key === ' ' || event.key === 'Space' || 
          event.key === 'ArrowUp' || event.key === 'ArrowDown' ||
          event.key === 'Enter' || event.key === 'Escape') {
        // These keys are commonly used for video control, pause carousel
        handleVideoInteraction();
        return;
      }
      
      if (event.key === 'ArrowLeft') {
        prevSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, allContent.length]);

  // Auto-scroll carousel every 2 seconds
  useEffect(() => {
    if (allContent.length <= 1 || isCarouselPaused || isVideoInteracting) return; // Don't auto-scroll if only one item, no items, paused, or video is being interacted with
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        // Loop back to start when reaching the end
        return prev >= allContent.length - 1 ? 0 : prev + 1;
      });
    }, 2000); // Auto-scroll every 2 seconds

    return () => clearInterval(interval);
  }, [allContent.length, isCarouselPaused, isVideoInteracting]);

  useEffect(() => {
    const initializeData = async () => {
      // On initial load, pass undefined to use default USA filter
      await fetchPackages(undefined); // This will use DEFAULT_COUNTRY_ID internally
      await fetchBooks(undefined);    // Then load books (which filters out package books)
    };
    
    initializeData();
    fetchLocations();
    fetchGrades();
    
    // Load states for United States by default
    handleCountryChange(DEFAULT_COUNTRY_ID);
  }, []);

  // Reset carousel when filters change (but don't refetch - that's handled by handleFilterChange)
  useEffect(() => {
    setCurrentSlide(0);
  }, [filters]);

  const fetchPackages = async (filterParams?: FilterState): Promise<void> => {
    try {
      setLoading(true);
      
      // Build query parameters for filtering
      const queryParams = new URLSearchParams();
      
      // Always include approval_status and is_active to get all packages
      queryParams.append('approval_status', 'all');
      queryParams.append('is_active', 'all');
      
      // On initial load (no filterParams or country is default), send country_id=230 for USA
      // When user explicitly changes filters, use their selection
      const countryToUse = filterParams?.country || DEFAULT_COUNTRY_ID;
      if (countryToUse) {
        queryParams.append('country_id', countryToUse);
      }
      
      if (filterParams?.search) {
        queryParams.append('search', filterParams.search);
      }
      if (filterParams?.grade) {
        queryParams.append('grade_id', filterParams.grade);
      }
      if (filterParams?.state) {
        queryParams.append('state_id', filterParams.state);
      }
      
      // Fetch content packages from the package service (same endpoint as admin panel)
      const url = `${apiUrls.packages.list}?${queryParams.toString()}`;
      console.log('📦 Fetching packages with filters:', url);
      
      const response = await fetch(url, {
        headers: getHttpHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Raw content packages response:', data);
      console.log('📦 Applied filters:', filterParams);
      console.log('📦 Query URL:', url);
      
      // Use the same extraction logic as admin panel (simple and reliable)
      let packagesData = data.packages || [];
      
      if (!packagesData.length && Array.isArray(data)) {
        // Fallback: if response is directly an array
        packagesData = data;
      }
      
      // Some APIs return array of { package: {...} } or {contentPackage: {...}}
      packagesData = packagesData.map((pkg: any) => pkg.package || pkg.contentPackage || pkg);

      console.log('📦 Number of packages returned:', packagesData.length);
      
      // If user is authenticated, check subscription status for each package
      if (isAuthenticated && user) {
        packagesData = await Promise.all(packagesData.map(async (pkg: ContentPackage) => {
          try {
            // Check if user has subscription to this package
            const subResponse = await fetch(apiUrls.subscriptions.byUser(user.id), {
              headers: getHttpHeaders()
            });
            
            if (subResponse.ok) {
              const subData = await subResponse.json();
              const hasSubscription = subData.subscriptions?.some((sub: any) => 
                sub.package_id === pkg.id && sub.status === 'active'
              );
              return { ...pkg, isSubscribed: hasSubscription };
            }
          } catch (error) {
            console.error('Error checking subscription for package:', pkg.id, error);
          }
          return { ...pkg, isSubscribed: false };
        }));
      } else {
        // For non-authenticated users, all packages are not subscribed
        packagesData = packagesData.map((pkg: ContentPackage) => ({ ...pkg, isSubscribed: false }));
      }
      
      setPackages(packagesData);
      setError('');
      
      // Always combine immediately with current books state
      // This ensures packages display even if books haven't loaded yet
      combineContent(packagesData, books, filterParams);
    } catch (err) {
      console.error('Error fetching content packages:', err);
      setError('Failed to load self-learning packages. Please try again.');
      setPackages([]);
      combineContent([], books, filterParams); // Clear packages on error
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async (filterParams?: FilterState): Promise<void> => {
    try {
      // Build query parameters for filtering
      const queryParams = new URLSearchParams();
      if (filterParams?.search) {
        queryParams.append('search', filterParams.search);
      }
      if (filterParams?.grade) {
        queryParams.append('grade', filterParams.grade);
      }
      if (filterParams?.country && filterParams.country !== DEFAULT_COUNTRY_ID) {
        queryParams.append('country', filterParams.country);
      }
      if (filterParams?.state) {
        queryParams.append('state', filterParams.state);
      }
      
      // Fetch books that are not part of any package
      const url = `${apiUrls.books.list}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log('📚 Fetching books with filters:', url);
      
      const response = await fetch(url, {
        headers: getHttpHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📚 Books response with filters:', data);
      console.log('📚 Applied filters:', filterParams);
      console.log('📚 Query URL:', url);
      
      let booksData = data.books || [];
      console.log('📚 Number of books returned:', booksData.length);
      
      // Don't show standalone books - only show video packages
      // Books are already included within the packages themselves
      console.log('📦 Hiding standalone books - showing only video packages');
      
      booksData = [];  // Don't show any standalone books
      
      // Transform books to match our interface and add type
      booksData = booksData.map((book: any) => {
        console.log('📖 Book data:', book);
        console.log('📖 Book video_link:', book.video_link);
        console.log('📖 Book image:', book.image);
        return {
          ...book,
          name: book.title, // Map title to name for consistency
          duration_months: 12, // Default access duration for books
          max_users: 1, // Default for individual books
          type: 'book',
          isPurchased: false, // Will be updated based on user purchase status
          video_link: book.video_link, // Explicitly preserve video_link
          image: book.image // Explicitly preserve image
        };
      });
      
      // If user is authenticated, check purchase status for each book
      if (isAuthenticated && user) {
        // TODO: Add logic to check if user has purchased individual books
        // This would require a new API endpoint or extending existing ones
      }
      
      setBooks(booksData);
      // Don't call combineContent here - packages will call it after fetching
      // This prevents race condition where books might load before packages
    } catch (err) {
      console.error('Error fetching books:', err);
      setBooks([]);
    }
  };

  const combineContent = (packagesData: ContentPackage[], booksData: StandaloneBook[], filterParams?: FilterState): void => {
    // Ensure packages always come first, then books
    const packageItems = packagesData.map(pkg => ({ ...pkg, type: 'package' as const }));
    const bookItems = booksData.map(book => ({ ...book, type: 'book' as const }));
    
    // Sort packages by approval status (approved first), then by name
    const sortedPackages = packageItems.sort((a, b) => {
      if (a.approval_status === 'approved' && b.approval_status !== 'approved') return -1;
      if (a.approval_status !== 'approved' && b.approval_status === 'approved') return 1;
      return a.name.localeCompare(b.name);
    });
    
    // Sort books by title
    const sortedBooks = bookItems.sort((a, b) => a.title.localeCompare(b.title));
    
    const combined: ContentItem[] = [
      ...sortedPackages,  // Packages first (approved packages prioritized)
      ...sortedBooks      // Books second (alphabetical order)
    ];
    
    console.log('📦 Combined content order:', combined.map(item => ({
      type: item.type,
      name: item.type === 'package' ? item.name : item.title,
      grade_id: item.grade_id,
      approved: item.type === 'package' ? item.approval_status : 'N/A'
    })));
    
    console.log('📊 Content summary:', {
      totalPackages: sortedPackages.length,
      totalBooks: sortedBooks.length,
      totalCombined: combined.length,
      currentFilters: filterParams || 'none'
    });
    
    setAllContent(combined);
  };

  // Helper function to get display name for both packages and books
  const getItemName = (item: ContentItem): string => {
    return item.type === 'package' ? (item as ContentPackage).name : (item as StandaloneBook).title;
  };

  // Helper function to get the appropriate image
  const getItemImage = (item: ContentItem): string => {
    const image = item.image;
    console.log('🖼️ Getting image for item:', item.type, 'image:', image);
    if (item.type === 'book') {
      const imagePath = image ? `/images/${image}` : '/images/default-book-thumbnail.jpg';
      console.log('📖 Book image path:', imagePath);
      return imagePath;
    }
    const imagePath = image || '/images/default-video-thumbnail.jpg';
    console.log('📦 Package image path:', imagePath);
    return imagePath;
  };

  const fetchLocations = async (): Promise<void> => {
    try {
      // Only fetch countries initially, states will be fetched when country is selected
      const countriesResponse = await locationService.getCountries();
      
      if (countriesResponse.success) {
        console.log('🌍 Setting countries:', countriesResponse.data);
        setCountries(countriesResponse.data || []);
      } else {
        console.error('Failed to fetch countries');
        setCountries([]);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchGrades = async (): Promise<void> => {
    try {
      const gradesResponse = await dataService.getGrades();
      if (gradesResponse.success) {
        setGrades(gradesResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching grades:', err);
    }
  };

  const applyFilters = async (): Promise<void> => {
    await fetchPackages(filters);
    await fetchBooks(filters);
  };

  const handleFilterChange = async (filterType: keyof FilterState, value: string): Promise<void> => {
    let normalizedValue = value;
    const newFilters = { ...filters };
    
    // Reset state when country changes
    if (filterType === 'country') {
      normalizedValue = value || DEFAULT_COUNTRY_ID; // Default to USA if empty
      newFilters.country = normalizedValue;
      newFilters.state = '';
      // Fetch states for the new country
      if (normalizedValue) {
        handleCountryChange(normalizedValue);
      } else {
        setStates([]);
      }
    } else {
      newFilters[filterType] = value;
    }
    
    setFilters(newFilters);
    // Automatically apply filters when they change
    await fetchPackages(newFilters);
  };

  const handleCountryChange = async (countryId: string): Promise<void> => {
    try {
      const statesResponse = await locationService.getStatesByCountry(countryId);
      if (statesResponse.success) {
        setStates(statesResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching states by country:', err);
    }
  };

  const clearFilters = async (): Promise<void> => {
    const resetFilters: FilterState = {
      search: '',
      grade: '',
      country: DEFAULT_COUNTRY_ID,
      state: ''
    };
    setFilters(resetFilters);
    await handleCountryChange(DEFAULT_COUNTRY_ID);
    // Pass undefined to use default USA filter
    await fetchPackages(undefined);
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDuration = (duration: number): string => {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <>
    <PageContainer>
              <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
          currentPage="videoLearning"
        />
      
      <MainContent>
        {/* Filter Section */}
        <FilterMain>
          <FilterTitle>
            <FiVideo />
            Find Self-Learning Content
          </FilterTitle>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>
                <FiSearch />
                Search Content
              </FilterLabel>
              <SearchField
                value={filters.search}
                onChange={(value) => handleFilterChange('search', value)}
                placeholder="Search packages, books, courses..."
                isLoading={loading}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiBookOpen />
                Country
              </FilterLabel>
              <FormSelect
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
              >
                <option value="">All Countries</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiTarget />
                State
              </FilterLabel>
              <FormSelect
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                disabled={!filters.country}
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiTarget />
                Grade
              </FilterLabel>
              <FormSelect
                value={filters.grade}
                onChange={(e) => handleFilterChange('grade', e.target.value)}
              >
                <option value="">All Grades</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel style={{ opacity: 0 }}>
                Actions
              </FilterLabel>
              <button
                onClick={() => clearFilters()}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  color: CSS_VARS.primaryColor,
                  border: `2px solid ${CSS_VARS.primaryColor}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  height: '44px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = CSS_VARS.primaryColor;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = CSS_VARS.primaryColor;
                }}
              >
                <FiX size={16} />
                Clear
              </button>
            </FilterGroup>
          </FilterGrid>
        </FilterMain>

        {/* Video Packages Carousel */}
        {loading ? (
          <LoadingState>Loading self-learning packages...</LoadingState>
        ) : error ? (
          <ErrorState>{error}</ErrorState>
        ) : allContent.length > 0 ? (
          <div style={{ marginTop: '15px' }}>
            {/* Compact Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)',
              borderRadius: '8px',
              padding: '10px 20px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 4px 15px rgba(37, 60, 140, 0.15)'
            }}>
              <div>
                <h2 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 0
                }}>
                  📚 Self-Learning Hub
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: '2px 0 0 0',
                  fontWeight: '500'
                }}>
                  Item {currentSlide + 1} of {allContent.length} • {packages.length} packages, {books.length} books
                  {(filters.grade || filters.state || filters.country) && (
                    <span style={{ 
                      marginLeft: '10px', 
                      fontSize: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      🔍 Filtered
                    </span>
                  )}
                  {isVideoInteracting && (
                    <span style={{ 
                      marginLeft: '10px', 
                      fontSize: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      ⏸️ Video Playing
                    </span>
                  )}
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginRight: '8px',
                  fontWeight: '600'
                }}>
                  {currentSlide + 1} / {allContent.length}
                </div>
                
                {/* Pause/Play Button */}
                <button
                  onClick={() => setIsCarouselPaused(!isCarouselPaused)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    marginRight: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={isCarouselPaused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
                >
                  {isCarouselPaused ? <FiPlay size={16} /> : <FiPause size={16} />}
                </button>
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: currentSlide === 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    color: currentSlide === 0 ? 'rgba(255, 255, 255, 0.4)' : 'white',
                    cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide === 0 ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== 0) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== 0) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                >
                  <FiChevronLeft size={16} />
                </button>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === packages.length - 1}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: currentSlide === packages.length - 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    color: currentSlide === packages.length - 1 ? 'rgba(255, 255, 255, 0.4)' : 'white',
                    cursor: currentSlide === packages.length - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide === packages.length - 1 ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== packages.length - 1) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== packages.length - 1) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Enhanced Package Display */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0 20px'
            }}>
              {visibleContent.map((item) => (
                <CarouselCard key={`${item.type}-${item.id}`}>
                  {/* Left: Book Image, Price, and Learn More */}
                  <ImageSection>
                    {(() => {
                      // For packages, show the first book's image and price
                      if (item.type === 'package') {
                        const packageItem = item as ContentPackage;
                        const firstBook = packageItem.packageBooks?.[0]?.book;
                        
                        if (firstBook) {
                          const bookImage = firstBook.image ? `/images/${firstBook.image}` : '/images/default-book-thumbnail.jpg';
                          const bookPrice = firstBook.price || 0;
                          
                          return (
                            <>
                              <BookImage 
                                src={bookImage}
                                alt={firstBook.title}
                              />
                              <PriceTag>
                                {formatPrice(bookPrice)}
                              </PriceTag>
                              <SecondaryButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Learn More clicked for package:', item);
                                  window.open(`${window.location.origin}?page=packageDetail&id=${item.id}`, '_blank');
                                }}
                                style={{ marginTop: '12px', width: '100%' }}
                              >
                                <FiInfo size={16} />
                                Learn More
                              </SecondaryButton>
                            </>
                          );
                        }
                      }
                      
                      // For standalone books, show book image and price
                      return (
                        <>
                          <BookImage 
                            src={getItemImage(item)} 
                            alt={getItemName(item)}
                          />
                          <PriceTag>
                            {formatPrice(item.price)}
                          </PriceTag>
                          <SecondaryButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Learn More clicked for book:', item);
                              window.open(`${window.location.origin}?page=bookDetail&id=${item.id}`, '_blank');
                            }}
                            style={{ marginTop: '12px', width: '100%' }}
                          >
                            <FiInfo size={16} />
                            Learn More
                          </SecondaryButton>
                        </>
                      );
                    })()}
                  </ImageSection>

                  {/* Middle: Video Player */}
                  <VideoSection className={isVideoInteracting ? 'video-active' : ''}>
                    {(() => {
                      // Get video link from the item
                      let videoLink = '';
                      if (item.type === 'package') {
                        // For packages, get video link from the first book in the package
                        const packageItem = item as ContentPackage;
                        videoLink = packageItem.packageBooks?.[0]?.book?.video_link || '';
                        console.log('📦 Package video_link:', videoLink);
                      } else {
                        // For standalone books, get video link directly
                        const bookItem = item as StandaloneBook;
                        videoLink = bookItem.video_link || '';
                        console.log('📖 Book video_link:', videoLink);
                        console.log('📖 Book item:', bookItem);
                      }

                      if (videoLink) {
                        // Display actual video
                        return (
                          <div 
                            style={{ 
                              width: '100%', 
                              height: '240px',
                              position: 'relative'
                            }}
                            onMouseEnter={handleVideoInteraction}
                            onMouseLeave={handleVideoInteractionEnd}
                            onClick={handleVideoInteraction}
                            onFocus={handleVideoInteraction}
                            onBlur={handleVideoInteractionEnd}
                          >
                            <iframe
                              width="100%"
                              height="240"
                              src={videoLink}
                              title={`${getItemName(item)} Video Preview`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                              onLoad={() => {
                                console.log('🎬 Video iframe loaded');
                              }}
                            />
                          </div>
                        );
                      } else {
                        // Display placeholder
                        return (
                          <div style={{
                            width: '100%',
                            height: '240px',
                            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#64748b',
                            textAlign: 'center'
                          }}>
                            <FiPlay size={40} style={{ marginBottom: '12px', opacity: 0.6 }} />
                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                              Video Preview
                            </h4>
                            <p style={{ fontSize: '12px', opacity: 0.8 }}>
                              Video preview coming soon
                            </p>
                          </div>
                        );
                      }
                    })()}
                    
                    {/* Video action button */}
                    <SecondaryButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        
                        // Get video link from the item
                        let videoLink = '';
                        if (item.type === 'package') {
                          // For packages, get video link from the first book in the package
                          const packageItem = item as ContentPackage;
                          videoLink = packageItem.packageBooks?.[0]?.book?.video_link || '';
                        } else {
                          // For standalone books, get video link directly
                          const bookItem = item as StandaloneBook;
                          videoLink = bookItem.video_link || '';
                        }

                        if (videoLink) {
                          // Pause carousel when video is opened
                          setIsCarouselPaused(true);
                          
                          // Open video in popup window
                          const videoWindow = window.open(videoLink, 'videoPreview', 'width=600,height=400,scrollbars=yes,resizable=yes');
                          
                          // Monitor if popup is closed or video ends
                          const checkVideoStatus = setInterval(() => {
                            if (videoWindow && videoWindow.closed) {
                              // Popup was closed, resume carousel
                              clearInterval(checkVideoStatus);
                              setIsCarouselPaused(false);
                            }
                          }, 1000);
                          
                          // Fallback: resume carousel after 5 minutes if popup is still open
                          setTimeout(() => {
                            clearInterval(checkVideoStatus);
                            if (!videoWindow || !videoWindow.closed) {
                              setIsCarouselPaused(false);
                            }
                          }, 300000); // 5 minutes fallback
                        } else {
                          // Show message if no video available
                          alert('Video preview not available for this item.');
                        }
                      }}
                    >
                      <FiVideo size={16} />
                      Play in full window
                    </SecondaryButton>

                    {/* Access Information - Moved from content section */}
                    <MetaInfo style={{ margin: '12px 0', width: '100%', padding: '12px' }}>
                      <MetaItem>
                        <FiClock size={14} />
                        {item.type === 'package' ? 
                          `${(item as ContentPackage).duration_months} month${(item as ContentPackage).duration_months !== 1 ? 's' : ''}` :
                          'Lifetime Access'
                        }
                      </MetaItem>
                      <MetaItem>
                        <FiBookOpen size={14} />
                        {item.type === 'package' ? 
                          `${(item as ContentPackage).packageBooks?.length || 0} books` :
                          'Single Book'
                        }
                      </MetaItem>
                      <MetaItem>
                        <FiUsers size={14} />
                        {item.type === 'package' ? 
                          `${(item as ContentPackage).max_users} user${(item as ContentPackage).max_users !== 1 ? 's' : ''} max` :
                          '1 user'
                        }
                      </MetaItem>
                    </MetaInfo>

                    {/* Enrollment Buttons - Under video with access info */}
                    <div style={{ width: '100%' }}>
                      <EnrollmentButton
                        item={{
                          id: item.id,
                          title: getItemName(item),
                          name: getItemName(item),
                          price: item.price,
                          discount_price: item.discount_price,
                          type: item.type,
                          image: getItemImage(item),
                          duration_months: item.type === 'package' ? (item as ContentPackage).duration_months : undefined,
                          max_users: item.type === 'package' ? (item as ContentPackage).max_users : undefined
                        }}
                        bookPrice={
                          item.type === 'package' 
                            ? (item as ContentPackage).packageBooks?.[0]?.book?.price || 0
                            : item.price
                        }
                        userId={user?.id}
                        addedToCart={addedToCart.has(item.id)}
                        onAddedToCartChange={(added) => {
                          if (added) {
                            setAddedToCart(prev => new Set(prev).add(item.id));
                          } else {
                            setAddedToCart(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(item.id);
                              return newSet;
                            });
                          }
                        }}
                        onEnrollmentSuccess={(type, enrollmentItem) => {
                          console.log('Enrollment success:', type, enrollmentItem);
                          if (type === 'subscription') {
                            // Refresh the data to update subscription status
                            fetchPackages();
                            fetchBooks();
                          }
                        }}
                      />

                      {/* View Details Button for Packages */}
                      {item.type === 'package' && onPackageDetailClick && (
                        <SecondaryButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onPackageDetailClick(item.id);
                          }}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            marginTop: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontWeight: '600'
                          }}
                        >
                          <FiInfo size={16} />
                          View Package Details
                        </SecondaryButton>
                      )}
                    </div>
                  </VideoSection>

                  {/* Right: Content Details */}
                  <ContentSection>
                    <ContentHeader>
                      <ContentTitle>
                        {getItemName(item)}
                      </ContentTitle>

                      <ContentSubtitle>
                        {item.type === 'package' ? 
                          `${(item as ContentPackage).duration_months} month${(item as ContentPackage).duration_months !== 1 ? 's' : ''} access` :
                          `Individual Book Access`
                        }
                      </ContentSubtitle>

                      <RatingSection>
                        {[...Array(5)].map((_, i) => (
                          <FiStar 
                            key={i} 
                            size={16} 
                            color={i < 4.5 ? CSS_VARS.secondaryColor : '#e2e8f0'} 
                          />
                        ))}
                        <span style={{ fontSize: '14px', color: CSS_VARS.textLight, marginLeft: '6px' }}>
                          (4.5 rating)
                        </span>
                      </RatingSection>
                    </ContentHeader>

                    <ContentDescription>
                      {(() => {
                        const cleanText = stripHtmlTags(item.description);
                        return cleanText.length > 300 
                          ? `${cleanText.substring(0, 300)}...`
                          : cleanText;
                      })()}
                    </ContentDescription>
                  </ContentSection>
                </CarouselCard>
              ))}
            </div>

            {/* Carousel Dots (only show up to 10 dots for better UX) */}
            {packages.length > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '8px',
                marginTop: '25px',
                flexWrap: 'wrap'
              }}>
                {allContent.slice(0, Math.min(10, allContent.length)).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    style={{
                      width: currentSlide === index ? '24px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      border: 'none',
                      background: currentSlide === index ? CSS_VARS.primaryColor : '#e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.background = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.background = '#e2e8f0';
                      }
                    }}
                  />
                ))}
                {packages.length > 10 && (
                  <span style={{ 
                    fontSize: '12px', 
                    color: CSS_VARS.textLight,
                    alignSelf: 'center',
                    marginLeft: '8px'
                  }}>
                    +{packages.length - 10} more
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <EmptyState>
            <FiVideo size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
            <h3>No Video Packages Found</h3>
            <p>
              We couldn't find any video learning packages matching your criteria.<br />
              Try adjusting your filters or clearing them to see all available packages.
            </p>
          </EmptyState>
        )}
      </MainContent>
    </PageContainer>
    <Footer 
      onBooksClick={onBooksClick}
      onVideoLearningClick={onVideoLearningClick}
      onBlogsClick={onBlogsClick}
      onFaqClick={onFaqClick}
      onFeedbackClick={onFeedbackClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
    />
    </>
  );
};

export default ContentPackagePage; 